/**
 * Performance monitoring utilities for tracking app performance
 */

// Performance metrics interface
interface PerformanceMetrics {
  loadTime: number
  renderTime: number
  bundleSize?: number
  memoryUsage?: number
  timestamp: number
}

// Performance observer for monitoring
class PerformanceMonitor {
  // private _metrics: PerformanceMetrics[] = [] // Reserved for future metrics collection
  private observer: PerformanceObserver | null = null

  constructor() {
    this.initializeObserver()
  }

  private initializeObserver() {
    if (typeof window !== 'undefined' && 'PerformanceObserver' in window) {
      this.observer = new PerformanceObserver((list) => {
        const entries = list.getEntries()
        entries.forEach((entry) => {
          this.handlePerformanceEntry(entry)
        })
      })

      // Observe navigation and resource timing
      this.observer.observe({ 
        entryTypes: ['navigation', 'resource', 'measure', 'paint'] 
      })
    }
  }

  private handlePerformanceEntry(entry: PerformanceEntry) {
    // Log significant performance events
    if (entry.entryType === 'navigation') {
      const navEntry = entry as PerformanceNavigationTiming
      console.log('Navigation Performance:', {
        domContentLoaded: navEntry.domContentLoadedEventEnd - navEntry.domContentLoadedEventStart,
        loadComplete: navEntry.loadEventEnd - navEntry.loadEventStart,
        totalTime: navEntry.loadEventEnd - navEntry.fetchStart,
      })
    }

    if (entry.entryType === 'paint') {
      console.log(`${entry.name}: ${entry.startTime}ms`)
    }
  }

  // Measure component render time
  measureRender(componentName: string, renderFn: () => void) {
    const startTime = performance.now()
    renderFn()
    const endTime = performance.now()
    
    const renderTime = endTime - startTime
    console.log(`${componentName} render time: ${renderTime.toFixed(2)}ms`)
    
    return renderTime
  }

  // Measure async operation time
  async measureAsync<T>(operationName: string, asyncFn: () => Promise<T>): Promise<T> {
    const startTime = performance.now()
    
    try {
      const result = await asyncFn()
      const endTime = performance.now()
      const duration = endTime - startTime
      
      console.log(`${operationName} completed in: ${duration.toFixed(2)}ms`)
      return result
    } catch (error) {
      const endTime = performance.now()
      const duration = endTime - startTime
      
      console.error(`${operationName} failed after: ${duration.toFixed(2)}ms`, error)
      throw error
    }
  }

  // Get memory usage (if available)
  getMemoryUsage() {
    if ('memory' in performance) {
      const memory = (performance as any).memory
      return {
        used: Math.round(memory.usedJSHeapSize / 1048576), // MB
        total: Math.round(memory.totalJSHeapSize / 1048576), // MB
        limit: Math.round(memory.jsHeapSizeLimit / 1048576), // MB
      }
    }
    return null
  }

  // Record custom metric
  recordMetric(name: string, value: number, unit = 'ms') {
    performance.mark(`${name}-start`)
    performance.mark(`${name}-end`)
    performance.measure(name, `${name}-start`, `${name}-end`)
    
    console.log(`Custom metric - ${name}: ${value}${unit}`)
  }

  // Get performance summary
  getSummary(): PerformanceMetrics | null {
    if (typeof window === 'undefined') return null

    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming
    if (!navigation) return null

    return {
      loadTime: navigation.loadEventEnd - navigation.fetchStart,
      renderTime: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
      memoryUsage: this.getMemoryUsage()?.used,
      timestamp: Date.now(),
    }
  }

  // Cleanup observer
  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }
}

// Create singleton instance
export const performanceMonitor = new PerformanceMonitor()

// React hook for performance monitoring
export const usePerformanceMonitor = () => {
  return {
    measureRender: performanceMonitor.measureRender.bind(performanceMonitor),
    measureAsync: performanceMonitor.measureAsync.bind(performanceMonitor),
    recordMetric: performanceMonitor.recordMetric.bind(performanceMonitor),
    getMemoryUsage: performanceMonitor.getMemoryUsage.bind(performanceMonitor),
    getSummary: performanceMonitor.getSummary.bind(performanceMonitor),
  }
}

// Performance timing decorator for functions
export function withPerformanceTiming<T extends (...args: any[]) => any>(
  fn: T,
  name?: string
): T {
  return ((...args: any[]) => {
    const functionName = name || fn.name || 'anonymous'
    const startTime = performance.now()
    
    try {
      const result = fn(...args)
      
      // Handle async functions
      if (result && typeof result.then === 'function') {
        return result.finally(() => {
          const endTime = performance.now()
          console.log(`${functionName} (async): ${(endTime - startTime).toFixed(2)}ms`)
        })
      }
      
      // Handle sync functions
      const endTime = performance.now()
      console.log(`${functionName}: ${(endTime - startTime).toFixed(2)}ms`)
      return result
    } catch (error) {
      const endTime = performance.now()
      console.error(`${functionName} failed after: ${(endTime - startTime).toFixed(2)}ms`, error)
      throw error
    }
  }) as T
}

// Bundle size monitoring
export const bundleMonitor = {
  // Track when chunks are loaded
  trackChunkLoad: (chunkName: string) => {
    console.log(`Chunk loaded: ${chunkName}`)
    performanceMonitor.recordMetric(`chunk-load-${chunkName}`, performance.now())
  },

  // Track feature initialization
  trackFeatureInit: (featureName: string) => {
    console.log(`Feature initialized: ${featureName}`)
    performanceMonitor.recordMetric(`feature-init-${featureName}`, performance.now())
  },

  // Get estimated bundle size from network timing
  getEstimatedBundleSize: () => {
    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[]
    const jsResources = resources.filter(r => r.name.endsWith('.js'))
    
    let totalSize = 0
    jsResources.forEach(resource => {
      // Estimate size from transfer time (rough approximation)
      const transferTime = resource.responseEnd - resource.responseStart
      const estimatedSize = transferTime * 100 // Very rough estimate
      totalSize += estimatedSize
    })
    
    return Math.round(totalSize / 1024) // KB
  },
}

// Development-only performance warnings
export const performanceWarnings = {
  // Warn about slow renders
  warnSlowRender: (componentName: string, renderTime: number) => {
    if (process.env.NODE_ENV === 'development' && renderTime > 16) {
      console.warn(
        `⚠️ Slow render detected: ${componentName} took ${renderTime.toFixed(2)}ms (>16ms)`
      )
    }
  },

  // Warn about memory leaks
  warnMemoryUsage: () => {
    if (process.env.NODE_ENV === 'development') {
      const memory = performanceMonitor.getMemoryUsage()
      if (memory && memory.used > 100) { // 100MB threshold
        console.warn(
          `⚠️ High memory usage detected: ${memory.used}MB used of ${memory.total}MB total`
        )
      }
    }
  },

  // Warn about large bundle chunks
  warnLargeChunk: (chunkName: string, size: number) => {
    if (process.env.NODE_ENV === 'development' && size > 500) { // 500KB threshold
      console.warn(
        `⚠️ Large chunk detected: ${chunkName} is ${size}KB (>500KB)`
      )
    }
  },
}